export interface EventSummary {
  id: string;
  slug: string;
  name: string;
  city: string;
  timezone: string;
  startsAt: string;
  endsAt: string;
  state: string;
  counts: {
    total: number;
    enl: number;
    res: number;
    verified: number;
  };
  sheetConnected: boolean;
  lastSheetSyncAt: string | null;
}

export interface ParticipantStatus {
  statsVerified: boolean;
  participationVerified: boolean;
  sheet: {
    row: number | null;
    status: "pending" | "syncing" | "synced" | "failed" | "not_configured";
    error: string | null;
  };
  /** Status of the Data!A/B write, tracked separately from the submission sync. */
  verification: {
    status: "pending" | "synced" | "failed" | "not_configured";
    error: string | null;
  };
}

export interface ScanVerification {
  attempted: boolean;
  applied: boolean;
  error: { code: string; message: string } | null;
}

export interface ParticipantMe {
  participant: {
    id: string;
    codename: string;
    faction: "ENL" | "RES";
    qrUrl: string;
    status: ParticipantStatus;
  };
  event: {
    id: string;
    slug: string;
    name: string;
    city: string;
    timezone: string;
    startsAt: string;
    endsAt: string;
    state: string;
    lastSheetSyncAt: string | null;
  };
  submissions: {
    start: { submittedAt: string; revision: number; sheetSyncedAt: string | null } | null;
    end: { submittedAt: string; revision: number; sheetSyncedAt: string | null } | null;
  };
  notifications: Array<{
    id: string;
    title: string;
    body: string;
    published_at: string;
    read_at: string | null;
  }>;
  wallet: {
    available: boolean;
    apnsConfigured: boolean;
    downloadUrls: {
      modern: string;
      fallback: string;
    };
  };
}

export interface AdminEvent {
  id: string;
  slug: string;
  name: string;
  city: string;
  timezone: string;
  starts_at: string;
  ends_at: string;
  state: string;
  spreadsheet_id: string | null;
  last_sheet_sync_at: string | null;
  participant_count?: number;
}

export interface AdminParticipant {
  id: string;
  codename: string;
  faction: "ENL" | "RES";
  hasStart: boolean;
  hasEnd: boolean;
  statsVerified: boolean;
  participationVerified: boolean;
  walletBarcode: {
    issued: boolean;
    enabled: boolean;
    format: "qr" | "aztec" | "pdf417" | "code128";
    message: string | null;
    altText: string | null;
  };
  sheet: ParticipantStatus["sheet"];
  verification: ParticipantStatus["verification"];
}

export interface AdminDashboard {
  event: AdminEvent;
  counts: {
    total: number;
    enl: number;
    res: number;
    failed: number;
    verified: number;
  };
  participants: AdminParticipant[];
  commands: Array<{
    id: string;
    participant_id: string;
    phase: string;
    status: string;
    attempts: number;
    last_error: string | null;
    updated_at: string;
  }>;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly requestId?: string,
  ) {
    super(message);
  }
}

function cookieValue(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const value = part.trim();
    if (value.startsWith(prefix)) return decodeURIComponent(value.slice(prefix.length));
  }
  return null;
}

export async function api<T>(
  path: string,
  init: RequestInit & { admin?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (init.admin && !["GET", "HEAD", "OPTIONS"].includes(init.method ?? "GET")) {
    const csrf = cookieValue("ifs_admin_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  const payload = (await response.json().catch(() => null)) as
    | {
        error?: { code?: string; message?: string };
        requestId?: string;
      }
    | null;
  if (!response.ok) {
    throw new ApiClientError(
      payload?.error?.message ?? `请求失败 (${response.status})`,
      response.status,
      payload?.error?.code ?? "request_failed",
      payload?.requestId,
    );
  }
  return payload as T;
}

export function postJson<T>(
  path: string,
  body: unknown,
  options: { admin?: boolean; idempotencyKey?: string } = {},
): Promise<T> {
  const headers = new Headers();
  // A fresh random key on every attempt can never dedupe a retry, so callers
  // either pass a stable key or let the server derive one from the payload.
  if (options.idempotencyKey) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }
  return api<T>(path, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    admin: options.admin,
  });
}
